import { at } from '../content/placement.js';
import { primitive } from './sprites.js';
import { facingTurn } from './art.js';
import { Container, Graphics, Matrix } from 'pixi.js';
import { cameraQuarter, projectDirection, projectWorld, standAt } from './camera.js';
import { CONFIG, SECURITY_GATE, GATES, CHECK_IN_BOARD, WAITING_BOARD, STATION_FOOTPRINTS } from '../domain/config.js';
import { queuePoint } from '../domain/queues.js';
import type { Rect } from '../domain/types.js';
import { MAIN_BOARD_TOP, KIOSK_BOARD_TOP, KIOSK_WIDTH, KIOSK_HEIGHT, mainBoardHeight, GATE_BOARD_SPAN } from './boardlayout.js';

/** Floor rectangles projected before extrusion keep fixtures aligned in every camera view. */
function fixtureBox(r: Rect, height: number, lift = 0, dark = 0x347e94, light = 0x55aabc, top = 0xeef4e6, alpha = 1) {
    const node = new Container(), g = new Graphics(), origin = projectWorld(r.x, r.y);
    const corners = [[-r.w / 2, -r.h / 2], [r.w / 2, -r.h / 2], [r.w / 2, r.h / 2], [-r.w / 2, r.h / 2]]
        .map(([x, y]) => { const p = projectWorld(r.x + x, r.y + y); return { x: p.x - origin.x, y: p.y - origin.y - lift }; });
    const faces = corners.map((p, i) => [p, corners[(i + 1) % 4]]).sort(([a, b], [c, d]) => a.y + b.y - c.y - d.y);
    for (const [a, b] of faces) g.beginFill((a.x + b.x) < 0 ? dark : light, alpha).drawPolygon([a.x, a.y, b.x, b.y, b.x, b.y - height, a.x, a.y - height]).endFill();
    g.beginFill(top, alpha).drawPolygon(corners.flatMap(p => [p.x, p.y - height])).endFill();
    node.addChild(g);
    standAt(node, r.x, r.y);
    return node;
}

function boardCase(r: Rect, height: number, lift: number) {
    const body = fixtureBox(r, height, lift, 0x315467, 0x507e91, 0xc9dbd7);
    const face = new Graphics(), direction = cameraQuarter() < 2 ? 1 : -1;
    const axis = projectDirection(direction, 0);
    face.beginFill(0xe8eadb).drawRoundedRect(-r.w / 2, -lift - height, r.w, height, 5).endFill();
    face.beginFill(0x173449).drawRoundedRect(-r.w / 2 + 3, -lift - height + 3, r.w - 6, height - 6, 4).endFill();
    if (r === CHECK_IN_BOARD && direction < 0) {
        for (const side of [-1, 1]) face.beginFill(0x456d7c).drawRoundedRect(side * r.w * 0.3 - 3, -lift - height + 9, 6, height - 18, 2).endFill();
        for (let row = 0; row < 3; row++) face.beginFill(0x315261).drawRoundedRect(-24, -lift - 15 - row * 5, 48, 2, 1).endFill();
    }
    const node = new Container();
    face.transform.setFromMatrix(new Matrix(axis.x, axis.y, 0, 1, 0, 0));
    node.addChild(face);
    standAt(node, r.x, r.y + direction * r.h / 2, 1);
    return [body, node];
}
/** The walk-through arch turns with the Security desk, so travellers always pass through it, not along it. */
export function metalDetector() {
    const { x, y } = SECURITY_GATE;
    const image = primitive('detector-arch', 110, facingTurn(CONFIG.stages[2].facing));
    standAt(image, x, y);
    return [image];
}

/** One post per floor point, with short rope runs; no sprite pairs floating outside the line. Laid out in the desk's
 *  own frame (across the counter, out toward its customers), so ropes turn with a map desk's facing. */
export function queueRopes(index: number, level: number) {
    const nodes: Container[] = [], lanes = Math.min(4, 2 + level), s = CONFIG.stages[index];
    const front = STATION_FOOTPRINTS[index].h / 2 + 66, back = front + 80;
    const rope = (from: { x: number; y: number }, to: { x: number; y: number }, color: number, sag: number) => {
        const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }, origin = projectWorld(mid.x, mid.y), a = projectWorld(from.x, from.y), b = projectWorld(to.x, to.y);
        const line = new Graphics();
        if (sag) line.lineStyle(3, color).moveTo(a.x - origin.x, a.y - origin.y - 22).quadraticCurveTo(0, -18, b.x - origin.x, b.y - origin.y - 22);
        else line.lineStyle(3, color).moveTo(a.x - origin.x, a.y - origin.y - 22).lineTo(b.x - origin.x, b.y - origin.y - 22);
        standAt(line, mid.x, mid.y, 1);
        return line;
    };
    if (index === 6) {
        const p = at(s, 26, front), post = primitive('queue-post', 28);
        standAt(post, p.x, p.y, 2);
        return [rope(p, at(s, 26, 0), 0x335b77, 0), post];
    }
    for (let boundary = 0; boundary <= lanes; boundary++) {
        const side = 26 - boundary * 52;
        const start = boundary === 0 || boundary === lanes || boundary % 2 ? front - 24 : front + 24;
        const end = boundary === 0 || boundary === lanes || boundary % 2 === 0 ? back + 24 : back - 24;
        const points = [start, (start + end) / 2, end].map(out => at(s, side, out));
        for (const p of points) { const post = primitive('queue-post', 28); standAt(post, p.x, p.y, 2); nodes.push(post); }
        for (let n = 0; n < points.length - 1; n++) nodes.push(rope(points[n], points[n + 1], 0x68ac9a, 1));
    }
    return nodes;
}

/** Retracted glass leaves leave two clear passenger lanes through each doorway. */
export function gateDoors(gates: number, row: readonly { x: number; y: number; door?: { x: number; y: number }; facing?: readonly number[] }[] = GATES) {
    const nodes: Container[] = [];
    for (const [index, gate] of row.entries()) {
        // Laid out along the gate's wall: `a` runs along the wall, `n` points into the terminal.
        const door = gate.door ?? { x: gate.x + 70, y: 330 }, [fx, fy] = gate.facing ?? [0, -1];
        const a = { x: -fy, y: fx }, n = { x: -fx, y: -fy }, vertical = Math.abs(a.y) > Math.abs(a.x);
        const at = (along: number, across = 0) => ({ x: door.x + a.x * along + n.x * across, y: door.y + a.y * along + n.y * across });
        const box = (along: number, across: number, w: number, h: number) => ({ ...at(along, across), w: vertical ? h : w, h: vertical ? w : h });
        for (const side of [-1, 1]) {
            nodes.push(fixtureBox(box(side * 48, 0, 12, 20), 86, 0, 0x789ba5, 0xc6d9d7, 0xfff4da));
            nodes.push(fixtureBox(box(side * 48, 0, 16, 24), 5, 0, 0x577b8c, 0xa6c3c7, 0xf5ead2));
            const trim = new Graphics(), direction = cameraQuarter() < 2 ? 1 : -1, axis = projectDirection(a.x * direction, a.y * direction);
            trim.beginFill(0xf6edd8).drawRoundedRect(-6, -84, 12, 80, 3).endFill();
            trim.beginFill(0xb7c9c5).drawRoundedRect(-4, -81, 2, 74, 1).endFill();
            trim.transform.setFromMatrix(new Matrix(axis.x, axis.y, 0, 1, 0, 0));
            const column = new Container(); column.addChild(trim);
            const post = at(side * 48, direction * 10);
            standAt(column, post.x, post.y, 1); nodes.push(column);
        }
        nodes.push(fixtureBox(box(0, 0, 108, 20), 10, 84, 0x7597a2, 0xc6d9d7, 0xfff4da));
        for (const side of [-1, 1]) {
            const open = index < gates, centre = side * (open ? 39 : 22);
            nodes.push(fixtureBox(box(centre, 0, open ? 14 : 42, 4), 76, 5, 0x63b1c7, 0xc3eef0, 0xf2ffff, 0.48));
            nodes.push(fixtureBox(box(side * (open ? 32 : 1), 0, 2, 5), 76, 5, 0x79a7b4, 0xb7d8dc, 0xffffff));
            nodes.push(fixtureBox(box(centre + 2, 3, 1, 1), 60, 12, 0xe6ffff, 0xe6ffff, 0xe6ffff, 0.65));
        }
        nodes.push(...boardCase(box(0, 0, GATE_BOARD_SPAN + 8, 8), 24, 92));
    }
    return nodes;
}

export function departureBoardFrames(gates: number) {
    const nodes: Container[] = [];
    const main = CHECK_IN_BOARD, kiosk = WAITING_BOARD;
    const mainHeight = (main.w - 14) * mainBoardHeight(gates) / 940 + 8;
    const mainLift = MAIN_BOARD_TOP - mainHeight;
    if (mainLift > 64) for (const side of [-1, 1]) nodes.push(fixtureBox({ x: main.x + side * 100, y: main.y, w: 8, h: 8 }, mainLift - 64, 64, 0x6d9bab, 0xa5c5c8, 0xe8eadb));
    nodes.push(...boardCase(main, mainHeight, mainLift));
    nodes.push(fixtureBox({ ...kiosk, w: 94 }, 6, 0, 0x548599, 0x83acb8, 0xe8eadb));
    const kioskHeight = (kiosk.w - 14) * KIOSK_HEIGHT / KIOSK_WIDTH + 8, kioskLift = KIOSK_BOARD_TOP - kioskHeight;
    nodes.push(fixtureBox({ ...kiosk, w: 18, h: 18 }, kioskLift, 0, 0x56879b, 0x95b9c2, 0xe8eadb));
    nodes.push(...boardCase({ ...kiosk, h: 12 }, kioskHeight, kioskLift));
    return nodes;
}
